const Soroban = require('./Soroban');
const {
    map,
    values,
    clone,
    cloneDeep,
    toString,
    isEmpty,
    memoize,
    sample,
    difference,
    keys,
    shuffle,
    range,
    minBy,
    maxBy,
} = require('lodash');
const defaultCombiningPercents = require('../utils/trainer/combiningPercents.json');
const JUMP_OVER_VALUES = [50, 100];
const MIN_ARR_LENGTH_OF_DIGIT_CAP = 2;

const DEFAULT_OPTIONS = {
    digitsLimit: 1,
    sumLimit: 50,
    jumpOver: false,
    prevNumbers: null,
    mirror: false,
    simple: [1],
    brothers: [],
    brothersOptions: {
        onlyAddition: false,
        onlySubtraction: false,
    },
    friends: [],
    friendsOptions: {
        onlyAddition: false,
        onlySubtraction: false,
    },
    brothersAndFriends: [],
    brothersAndFriendsOptions: {
        onlyAddition: false,
        onlySubtraction: false,
    },
    amountOfActions: 2,
    combining: false,
    combiningDigitCapacities: [],
    fractions: false,
    fractionalPart: 1,
    onlyPositiveAnswers: false,
    onlyNegativeAnswers: false,
    interimAnswers: false,
    stepsWithoutMainFormula: 0,
    isMainFormulaAlreadyInExample: false,
};

const DEFAULT_NUM = 0;

/*  This const defines thresholds in max number of actions without specified example case
    The structure is like the following:
    `mainFormulaKey`: {
        `caseKey`: `numberOfStepsWithoutMainFormula`,
        ...
        default: `numberOfStepsWithoutMainFormula`
    }
    Case keys can be any number or string, e.g.: 1, 2, '1,2', '1,2,3,4'. For example, if we have mainFormula === 'brothers'
    and options array [1, 2, 3] then the key will be '1,2,3'.
    Default is for all other options that are not specified.

    `numberOfStepsWithoutMainFormula` is what it's named =)

    If we have brothers [1, 2, 3, 4] and corresponding threshold equal to 4
    then we will see brothers in example on every 5(!) steps as we start counting from 0

    It's not necessary to specify all possible cases (or even all mainFormulas, if it's absent then we get random number).
    Generation thresholds do not affect 'simple' rule as it's default and the only one if it's the main one.
 */
const GENERATION_THRESHOLDS = {
    brothers: {
        1: 3,
        2: 3,
        3: 3,
        4: 3,
        default: 4,
    },
    friends: {
        1: 3,
        default: 4,
    },
    brothersAndFriends: {
        default: 4,
    },
};

const arrayToNum = memoize(numArray => {
    let num = 0;
    let pow = 1;
    for (let index = 0; index < numArray.length; index++) {
        num += numArray[index] * pow;
        pow *= 10;
    }
    return num;
});

module.exports = class AddSubGenerator {
    constructor(num, options) {
        this.currentDigitsLimit = 1;
        this.quantity = 0;
        this.options = {};
        options = options || {};
        for (let option in DEFAULT_OPTIONS) {
            this.options[option] = cloneDeep(options[option]) || DEFAULT_OPTIONS[option];
        }
        this.options.simple = this.options.simple.sort();
        if (!this.options.onlyNegativeAnswers && !this.options.onlyPositiveAnswers) {
            this.options.onlyNegativeAnswers = !!Math.floor(Math.random() * 2);
            this.options.onlyPositiveAnswers = !this.options.onlyNegativeAnswers;
        }
        this.num = num || DEFAULT_NUM;
        this.lastNum = null;
        this.numArray = this.num
            .toString(10)
            .split('')
            .map(Number)
            .reverse();
        if (this.options.digitsLimit - this.numArray.length > 0) {
            this.numArray.push(...Array(this.options.digitsLimit - this.numArray.length).fill(0));
        }
        if (this.options.jumpOver) {
            this.jumpOverOperations = new Array(options.amountOfActions).fill(1);
        }
        if (this.options.interimAnswers) {
            this.interimOperations = new Array(options.amountOfActions).fill(0).map((operation, operationIndex) => {
                return (this.options.onlyNegativeAnswers && operationIndex <= 1) ||
                    operationIndex < 1 ||
                    operationIndex >= options.amountOfActions - 1 ||
                    !((operationIndex - this.options.onlyNegativeAnswers) % 2)
                    ? 0
                    : 1;
            });
        }
        const defineSelectedFormulas = () => {
            if (this.options.brothersAndFriends.length) {
                return 'brothersAndFriends';
            } else if (this.options.friends.length) {
                return 'friends';
            } else if (this.options.brothers.length) {
                return 'brothers';
            }
            return 'simple';
        };
        const getMainFormulaThreshold = () => {
            if (!GENERATION_THRESHOLDS[this.options.mainFormula]) {
                return undefined;
            }
            const mainThresholdKey = this.options[this.options.mainFormula].join(',');
            if (!GENERATION_THRESHOLDS[this.options.mainFormula][mainThresholdKey]) {
                return GENERATION_THRESHOLDS[this.options.mainFormula]['default'];
            } else {
                return GENERATION_THRESHOLDS[this.options.mainFormula][mainThresholdKey];
            }
        };
        this.options.mainFormula = defineSelectedFormulas();
        this.options.mainFormulaThreshold = getMainFormulaThreshold();
    }

    generateCurrentDigitsLimit(digitsLimit, combiningPercents) {
        if (this.options.fractions) {
            digitsLimit -= +this.options.fractionalPart;
        }
        let randomPercent = Math.floor(Math.random() * 100);
        let remainingPercent = 100;
        for (let i = digitsLimit; i > 0; i--) {
            if (randomPercent >= (remainingPercent -= combiningPercents[digitsLimit][i])) {
                this.currentDigitsLimit = i;
                if (this.options.fractions) {
                    this.currentDigitsLimit += +this.options.fractionalPart;
                }
                break;
            }
        }
    }

    changeCombiningPercents() {
        let combiningPercents = cloneDeep(defaultCombiningPercents);
        let capacityLimit = this.options.digitsLimit;
        if (this.options.combiningDigitCapacities.length >= MIN_ARR_LENGTH_OF_DIGIT_CAP) {
            let capacitiesWhichNeedToResetChance = difference(
                map(keys(combiningPercents[capacityLimit]), key => +key),
                this.options.combiningDigitCapacities
            );
            if (capacitiesWhichNeedToResetChance.length) {
                capacitiesWhichNeedToResetChance.forEach(capacity => {
                    combiningPercents[capacityLimit][capacity] = 0;
                });
                let newChancePercents = values(
                    cloneDeep(defaultCombiningPercents[this.options.combiningDigitCapacities.length])
                );
                this.options.combiningDigitCapacities.forEach((capacity, index) => {
                    combiningPercents[capacityLimit][capacity] = newChancePercents[index];
                });
            }
        }
        return combiningPercents;
    }

    nextNum(index, numArray, results, unwantedResults, rules) {
        const isJumpOverEnabled = !!this.options.jumpOver;
        const isJumpOverAndOneDigit = isJumpOverEnabled && this.options.digitsLimit === 1;
        const isMoreDigitNumberNeeded =
            !this.quantity &&
            this.options.onlyPositiveAnswers &&
            !this.options.mirror &&
            (isJumpOverAndOneDigit ||
                (this.options.mainFormula === 'friends' && this.options.friendsOptions.onlySubtraction) ||
                (this.options.mainFormula === 'brothersAndFriends' &&
                    this.options.brothersAndFriendsOptions.onlySubtraction));
        if (isMoreDigitNumberNeeded) {
            let startNumber;
            const numberOrder = Math.pow(10, this.options.digitsLimit);
            let additionalNumber =
                Math.random() > 0.5
                    ? Math.floor(Math.random() * numberOrder)
                    : -Math.floor(Math.random() * numberOrder);
            if (this.options.friends.length && isJumpOverAndOneDigit) {
                const additionalNumbersForNegativeFriends = this.options.friends.map(num => num - 1);
                const additionalNumbersForPositiveFriends = this.options.friends.map(num => -num);
                if (this.options.friendsOptions.onlySubtraction) {
                    additionalNumber = sample(additionalNumbersForNegativeFriends);
                } else if (this.options.friendsOptions.onlyAddition) {
                    additionalNumber = sample(additionalNumbersForPositiveFriends);
                } else {
                    additionalNumber = sample([
                        ...additionalNumbersForPositiveFriends,
                        ...additionalNumbersForNegativeFriends,
                    ]);
                }
            }
            if (isJumpOverAndOneDigit) {
                startNumber = this.options.jumpOver;
            } else {
                const AVAILABLE_FIRST_DIGITS_FOR_ONLY_SUBTRACTION = [3, 7, 8];
                startNumber =
                    sample(AVAILABLE_FIRST_DIGITS_FOR_ONLY_SUBTRACTION) * numberOrder +
                    (isJumpOverEnabled ? this.options.jumpOver : 0);
            }
            this.options.sumLimit = startNumber + numberOrder;
            this.num = startNumber + additionalNumber;
            this.numArray = this.num
                .toString(10)
                .split('')
                .map(Number)
                .reverse();

            this.quantity++;
            this.lastNum = this.num;
            return this.num;
        }

        const firstTime = index === undefined;

        if (this.options.combining && (!this.options.onlyNegativeAnswers || this.num < 0)) {
            this.generateCurrentDigitsLimit(this.options.digitsLimit, this.changeCombiningPercents());
        } else {
            this.currentDigitsLimit = this.options.digitsLimit;
        }

        index = index !== undefined ? index : this.currentDigitsLimit - 1;
        numArray = numArray || clone(this.numArray);
        results = results || [];
        rules = rules || [];
        unwantedResults = unwantedResults || [];

        if (index < 0) {
            let resultNum = arrayToNum(numArray);
            let num = resultNum - this.num;
            if (
                num === resultNum &&
                !this.quantity &&
                this.options.friendsOptions.onlySubtraction &&
                this.options.practiceRule
            ) {
                num += (this.options.amountOfActions * Math.pow(10, this.currentDigitsLimit)) / 2;
                resultNum = num;
                numArray = `${num}`
                    .split('')
                    .map(Number)
                    .reverse();
            }
            let result = cloneDeep({
                num,
                rules,
                numArray,
                resultNum,
            });
            if (this.options.onlyNegativeAnswers) {
                if (!this.num && resultNum > 0) {
                    this.addResult(result, results, unwantedResults);
                }
                if (this.num && this.options.interimAnswers ? resultNum !== 0 : resultNum <= 0) {
                    this.addResult(result, results, unwantedResults);
                }
            } else if (this.options.onlyPositiveAnswers) {
                if (!this.num && resultNum > 0) {
                    this.addResult(result, results, unwantedResults);
                }
                if (this.num && this.options.interimAnswers ? resultNum !== 0 : resultNum >= 0) {
                    this.addResult(result, results, unwantedResults);
                }
            } else {
                this.addResult(result, results, unwantedResults);
            }
            return;
        }

        let nums = this.getFilteredNums(index, numArray, rules);
        if (
            this.options.jumpOver &&
            this.jumpOverOperations[this.quantity] &&
            !!this.num &&
            (!this.options.onlyNegativeAnswers || this.num < 0)
        ) {
            if (this.options.jumpOver === 50) {
                nums = this.generateJumpOverThroughFifty(nums, index) || nums;
            } else {
                nums = this.generateJumpOverThroughHundred(nums, index) || nums;
            }
        }
        for (let i = 0; i < nums.length; i++) {
            let num = nums[i];
            let newNumArray = this.digitTransfer(index, num.value, numArray);
            let newRules = clone(rules);
            newRules.push(num);
            this.nextNum(index - 1, newNumArray, results, unwantedResults, newRules);
            if (results.length && this.options.digitsLimit * i >= nums.length) {
                break;
            }
        }
        if (firstTime) {
            return this.getNumFromResults(results, unwantedResults);
        }
    }

    addResult(result, results, unwantedResults) {
        if (Math.abs(result.resultNum) >= this.options.sumLimit) {
            return;
        }

        if (results.length && result.rules.some(rule => rule.value === 0)) {
            return unwantedResults.push(result);
        }

        if (Math.abs(result.num) === Math.abs(this.lastNum) && (result.num !== this.lastNum || Math.random() > 0.25)) {
            return unwantedResults.push(result);
        }
        const { result: jumpOverOperation } = this.getJumpOverOperation(
            result.resultNum,
            result.rules,
            result.resultNum - result.num
        );

        if (this.options.jumpOver) {
            if (jumpOverOperation === 0) {
                return unwantedResults.push(result);
            }
            if (jumpOverOperation !== this.options.jumpOver) {
                return;
            }
        } else if (JUMP_OVER_VALUES.includes(jumpOverOperation)) {
            return unwantedResults.push(result);
        }

        const isSomeRuleEqualsMainFormula = result.rules.some(rule => rule.type === this.options.mainFormula);
        if (!isSomeRuleEqualsMainFormula) {
            return unwantedResults.push(result);
        }

        if (this.options.interimAnswers) {
            if (this.interimOperations[this.quantity]) {
                if (this.options.onlyNegativeAnswers ? result.resultNum < 0 : result.resultNum > 0) {
                    return unwantedResults.push(result);
                }
            } else {
                if (this.options.onlyNegativeAnswers ? result.resultNum > 0 : result.resultNum < 0) {
                    return unwantedResults.push(result);
                }
            }
        }

        const isSimpleOptionAvailable = result => {
            const simpleOptionsLength = this.options.simple.length;
            const lastSelectedValue = this.options.simple[simpleOptionsLength - 1];
            const isNumberNotEqualToSelectedAvailableOnFirstPosition =
                this.quantity === 0 && this.options.amountOfActions > 2 && lastSelectedValue !== 9;
            const isNumberGoesThroughLastSelectedValue =
                (result.resultNum > lastSelectedValue && this.num < lastSelectedValue) ||
                (result.resultNum < lastSelectedValue && this.num > lastSelectedValue);
            return simpleOptionsLength === 2
                ? simpleOptionsLength === lastSelectedValue ||
                      Math.abs(result.num) === lastSelectedValue ||
                      isNumberGoesThroughLastSelectedValue ||
                      isNumberNotEqualToSelectedAvailableOnFirstPosition
                : true;
        };

        if (this.options.mainFormula === 'simple' && !isSimpleOptionAvailable(result)) {
            return unwantedResults.push(result);
        }

        return results.push(result);
    }

    getFilteredNums(index, numArray, rules) {
        let min = this.getMinForDigit(index, numArray);
        let max = this.getMaxForDigit(index, numArray);

        let nums = AddSubGenerator.generateNumsForDigit(numArray[index])
            .filter(num => num.value >= min && num.value <= max)
            .filter(num => this.isNumberAvailable(num))
            .filter(num => this.isOnlyNum(num))
            .filter(num => this.isRightSign(num, rules));

        const isFriendsExample = this.options.mainFormula === 'friends';

        if (isFriendsExample && this.options.friendsOptions.onlySubtraction) {
            const needsToInsertFriendOnNextStep =
                !this.options.isMainFormulaAlreadyInExample &&
                this.options.stepsWithoutMainFormula === this.options.mainFormulaThreshold;
            if (needsToInsertFriendOnNextStep) {
                const isAllowFriendOnNextStep = num => {
                    const getNumberWithZeroOnCurrentCapacityPosition = num => {
                        const partBeforeZero = num.toString().slice(0, -index - 1);
                        const partAfterZero = index !== 0 ? num.toString().slice(-index) : '';
                        return +(partBeforeZero + '0' + partAfterZero);
                    };

                    const maxFriend = Math.max(...this.options.friends);
                    const availableResults = range(getNumberWithZeroOnCurrentCapacityPosition(this.num), maxFriend - 1);
                    return num !== 0 && availableResults.includes(this.num + num.value * Math.pow(10, index));
                };

                const numsThatAllowFriendOnNextStep = nums.filter(isAllowFriendOnNextStep);
                if (numsThatAllowFriendOnNextStep.length) {
                    nums = numsThatAllowFriendOnNextStep;
                }
            }
        }

        let expectedNumSign = '+';
        if (
            (this.options.onlyNegativeAnswers &&
                (this.options.interimAnswers ? !this.interimOperations[this.quantity] : true)) ||
            (this.options.onlyPositiveAnswers &&
                (this.options.interimAnswers ? this.interimOperations[this.quantity] : false))
        ) {
            expectedNumSign = '-';
        }

        const isOneDigitExample = this.options.digitsLimit === 1;

        const newNums = nums.filter(num => this.isOperationAvailable(numArray, index, num, expectedNumSign));
        const isLastNumberOfDigit = index !== this.options.digitsLimit - 1;
        const isPossibleToChangeNumsToNewNums =
            (isLastNumberOfDigit && newNums.length) || newNums.some(num => num.value !== 0);
        const shouldChangeNumsToNewNums =
            !this.options.combining &&
            (!isOneDigitExample || !isFriendsExample || this.quantity > this.options.amountOfActions / 2 - 1.5) &&
            isPossibleToChangeNumsToNewNums;
        if (shouldChangeNumsToNewNums) {
            nums = newNums;
        } else if (this.options.combining) {
            this.currentDigitsLimit--;
        }

        if (
            index + 1 === this.currentDigitsLimit &&
            !(this.options.fractions && this.currentDigitsLimit === +this.options.fractionalPart + 1)
        ) {
            nums = nums.filter(num => num.value !== 0);
        }

        return shuffle(nums);
    }

    getMinForDigit(index, numArray) {
        let minNum = -this.options.sumLimit - arrayToNum(numArray);
        let minNumLength = minNum.toString(10).length - 1;
        if (minNumLength < index + 1) {
            return 0;
        }
        if (minNumLength === index + 1) {
            return -Number(minNum.toString(10)[1]);
        }
        return numArray.every(num => num === 0) && !this.num ? -numArray[index] : -9;
    }

    getMaxForDigit(index, numArray) {
        let maxNum = this.options.sumLimit - arrayToNum(numArray);
        let maxNumLength = maxNum.toString(10).length;
        if (maxNumLength < index + 1) {
            return 0;
        }
        if (maxNumLength === index + 1) {
            return Number(maxNum.toString(10)[0]);
        }
        return !this.num && this.options.onlyNegativeAnswers ? 8 : 9;
    }

    isNumberAvailable(num) {
        const isSimpleNumAvailable = numValue => {
            const simpleOptionsLength = this.options.simple.length;
            return simpleOptionsLength === 2
                ? Math.abs(numValue) <= this.options.simple[1]
                : this.options.simple.includes(Math.abs(numValue));
        };
        switch (num.type) {
            case 'simple':
                return (this.options.simple.length && isSimpleNumAvailable(num.value)) || num.value === 0;
            case 'brothers':
                return this.options.brothers.length && this.options.brothers.includes(Math.abs(num.value));
            case 'friends':
                return (
                    (this.options.friends.length && this.options.friends.includes(Math.abs(num.value))) ||
                    num.value === 0
                );
            case 'brothersAndFriends':
                return (
                    this.options.brothersAndFriends.length &&
                    this.options.brothersAndFriends.includes(Math.abs(num.value))
                );
        }
    }

    isOnlyNum(num) {
        if (num.type === 'brothers' && this.options.brothersOptions.onlySubtraction) {
            return num.value < 0;
        }
        if (num.type === 'brothers' && this.options.brothersOptions.onlyAddition) {
            return num.value > 0;
        }
        if (num.type === 'friends' && this.options.friendsOptions.onlySubtraction) {
            return num.value < 0;
        }
        if (num.type === 'friends' && this.options.friendsOptions.onlyAddition) {
            return num.value > 0;
        }
        if (num.type === 'brothersAndFriends' && this.options.brothersAndFriendsOptions.onlySubtraction) {
            return num.value < 0;
        }
        if (num.type === 'brothersAndFriends' && this.options.brothersAndFriendsOptions.onlyAddition) {
            return num.value > 0;
        }
        return true;
    }

    isRightSign(num, rules) {
        if (rules.some(rule => rule.value < 0)) {
            return num.value <= 0;
        } else if (rules.some(rule => rule.value > 0)) {
            return num.value >= 0;
        }
        return true;
    }

    isOperationAvailable(numArray, index, num, expectedNumSign) {
        let newNum,
            carryFlag = false;
        const isFirstNumber = !this.quantity;
        if (index === this.options.digitsLimit - 1) {
            newNum = (numArray[index] + num.value) % 10;
            carryFlag = (arrayToNum(numArray) + num.value * Math.pow(10, index)) / Math.pow(10, index + 1) >= 1;
        } else {
            newNum = this.digitTransfer(index, num.value, numArray)[this.options.digitsLimit - 1];
        }
        if (expectedNumSign === '-') {
            switch (newNum) {
                case 8:
                    return !this.options.friendsOptions.onlyAddition && this.options.friends.some(num => num === 9);
                case 7:
                    return (
                        !this.options.friendsOptions.onlyAddition &&
                        this.options.friends.some(num => num <= 9 && num >= 8)
                    );
                case 6:
                    return (
                        !this.options.friendsOptions.onlyAddition &&
                        this.options.friends.some(num => num <= 9 && num >= 7)
                    );
                case 5:
                    return (
                        !this.options.friendsOptions.onlyAddition &&
                        this.options.friends.some(num => num <= 9 && num >= 6)
                    );
                case 4:
                    return (
                        (!this.options.friendsOptions.onlyAddition && this.options.friends.some(num => num === 5)) ||
                        (!this.options.brothersAndFriendsOptions.onlyAddition &&
                            this.options.brothersAndFriends.some(num => num <= 9 && num >= 6))
                    );
                case 3:
                    return (
                        (!this.options.friendsOptions.onlyAddition &&
                            this.options.friends.some(num => num === 9 || (num <= 5 && num >= 4))) ||
                        (!this.options.brothersAndFriendsOptions.onlyAddition &&
                            this.options.brothersAndFriends.some(num => num <= 8 && num >= 6))
                    );
                case 2:
                    return (
                        (!this.options.friendsOptions.onlyAddition &&
                            this.options.friends.some(num => (num <= 9 && num >= 8) || (num <= 5 && num >= 3))) ||
                        (!this.options.brothersAndFriendsOptions.onlyAddition &&
                            this.options.brothersAndFriends.some(num => num === 7 || num === 6))
                    );
                case 1:
                    return (
                        (!this.options.friendsOptions.onlyAddition &&
                            this.options.friends.some(num => (num <= 9 && num >= 7) || (num <= 5 && num >= 2))) ||
                        (!this.options.brothersAndFriendsOptions.onlyAddition &&
                            this.options.brothersAndFriends.some(num => num === 6))
                    );
                case 0:
                    return !this.options.friendsOptions.onlyAddition && this.options.friends.length;
                default:
                    return true;
            }
        } else if (
            expectedNumSign === '+' &&
            this.options.mainFormula !== 'simple' &&
            (isFirstNumber || this.options.mainFormula !== 'brothers') &&
            (!carryFlag || this.options.interimAnswers)
        ) {
            switch (newNum) {
                case 1:
                    return (
                        (!this.options.brothersOptions.onlySubtraction &&
                            this.options.brothers.some(num => num === 4)) ||
                        (!this.options.friendsOptions.onlySubtraction && this.options.friends.some(num => num === 9))
                    );
                case 2:
                    return (
                        (!this.options.brothersOptions.onlySubtraction &&
                            this.options.brothers.some(num => num >= 3 && num <= 4)) ||
                        (!this.options.friendsOptions.onlySubtraction &&
                            this.options.friends.some(num => num >= 8 && num <= 9))
                    );
                case 3:
                    return (
                        (!this.options.brothersOptions.onlySubtraction &&
                            this.options.brothers.some(num => num >= 2 && num <= 4)) ||
                        (!this.options.friendsOptions.onlySubtraction &&
                            this.options.friends.some(num => num >= 7 && num <= 9))
                    );
                case 4:
                    return (
                        (!this.options.brothersOptions.onlySubtraction &&
                            this.options.brothers.some(num => num >= 1 && num <= 4)) ||
                        (!this.options.friendsOptions.onlySubtraction &&
                            this.options.friends.some(num => num >= 6 && num <= 9))
                    );
                case 5:
                    return (
                        (!this.options.brothersOptions.onlyAddition &&
                            this.options.brothers.some(num => num >= 1 && num <= 4)) ||
                        (!this.options.friendsOptions.onlySubtraction && this.options.friends.some(num => num === 5)) ||
                        (!this.options.brothersAndFriendsOptions.onlySubtraction &&
                            this.options.brothersAndFriends.some(num => num >= 6 && num <= 9))
                    );
                case 6:
                    return (
                        (!this.options.brothersOptions.onlyAddition &&
                            this.options.brothers.some(num => num >= 2 && num <= 4)) ||
                        (!this.options.friendsOptions.onlySubtraction &&
                            this.options.friends.some(num => num === 9 || (num >= 4 && num <= 5))) ||
                        (!this.options.brothersAndFriendsOptions.onlySubtraction &&
                            this.options.brothersAndFriends.some(num => num >= 6 && num <= 8))
                    );
                case 7:
                    return (
                        (!this.options.brothersOptions.onlyAddition &&
                            this.options.brothers.some(num => num >= 3 && num <= 4)) ||
                        (!this.options.friendsOptions.onlySubtraction &&
                            this.options.friends.some(num => (num >= 8 && num <= 9) || (num >= 3 && num <= 5))) ||
                        (!this.options.brothersAndFriendsOptions.onlySubtraction &&
                            this.options.brothersAndFriends.some(num => num >= 6 && num <= 7))
                    );
                case 8:
                    return (
                        (!this.options.brothersOptions.onlyAddition && this.options.brothers.some(num => num === 4)) ||
                        (!this.options.friendsOptions.onlySubtraction &&
                            this.options.friends.some(num => (num >= 7 && num <= 9) || (num >= 2 && num <= 5))) ||
                        (!this.options.brothersAndFriendsOptions.onlySubtraction &&
                            this.options.brothersAndFriends.some(num => num === 6))
                    );
                case 9:
                    return !this.options.friendsOptions.onlySubtraction && !!this.options.friends.length;
                default:
                    return false;
            }
        }
        return true;
    }

    getNumFromResults(results, unwantedResults) {
        if (!results.length) {
            results = clone(unwantedResults);
            const resultsWithoutZero = results.filter(result => result.resultNum !== 0);
            results = resultsWithoutZero.length ? resultsWithoutZero : results;
            const resultsWithoutInverseNumber = results.filter(result => result.num !== -this.lastNum);
            results = resultsWithoutInverseNumber.length ? resultsWithoutInverseNumber : results;
            const negativeResults = results.filter(result => result.resultNum < 0);
            const positiveResults = results.filter(result => result.resultNum > 0);

            const resultsThatDontGenerateJumpOver = results.filter(result => {
                const { result: jumpOverOperation } = this.getJumpOverOperation(
                    result.resultNum,
                    result.rules,
                    result.resultNum - result.num
                );
                return !JUMP_OVER_VALUES.includes(jumpOverOperation);
            });

            if (!this.options.jumpOver && resultsThatDontGenerateJumpOver.length) {
                results = resultsThatDontGenerateJumpOver;
            }

            const resultsWithMainFormula = results.filter(result =>
                result.rules.some(rule => rule.type === this.options.mainFormula)
            );
            results = resultsWithMainFormula.length ? resultsWithMainFormula : results;

            if (
                this.options.interimAnswers &&
                !this.interimOperations[this.quantity] &&
                ((this.options.onlyNegativeAnswers && this.quantity > 1) || this.quantity >= 1)
            ) {
                if (this.options.onlyNegativeAnswers) {
                    results = negativeResults.length ? negativeResults : [minBy(results, 'resultNum')];
                } else if (!this.options.onlyNegativeAnswers && this.num < 0) {
                    results = positiveResults.length ? positiveResults : [maxBy(results, 'resultNum')];
                }
            } else if (this.options.interimAnswers && this.interimOperations[this.quantity]) {
                if (this.options.onlyNegativeAnswers && this.num < 0) {
                    results = positiveResults.length ? positiveResults : results;
                } else if (!this.options.onlyNegativeAnswers && this.num > 0) {
                    results = negativeResults.length ? negativeResults : results;
                }
            }
        }

        if (this.options.prevNumbers) {
            let newResults = results.filter(num => num.num !== this.options.prevNumbers[this.quantity]);
            if (newResults.length > 0) {
                results = newResults;
                this.options.prevNumbers = null;
            }
        }

        let num = this.getRandomNumFromResults(results);

        if (num.rules.find(rule => rule.type === this.options.mainFormula)) {
            this.options.isMainFormulaAlreadyInExample = true;
        }

        this.numArray = clone(num.numArray);
        this.num = arrayToNum(num.numArray);
        this.quantity++;
        this.lastNum = num.num;
        return num.num;
    }

    getRandomResultAssumingThreshold(results) {
        if (this.options.mainFormula !== 'simple') {
            if (this.options.stepsWithoutMainFormula >= this.options.mainFormulaThreshold) {
                let mainFormulaNumbers = results.filter(number =>
                    number.rules.some(rule => rule.type === this.options.mainFormula)
                );
                if (mainFormulaNumbers.length) {
                    return { num: sample(mainFormulaNumbers), isMain: true };
                }
            }
        }
        return { num: sample(results), isMain: false };
    }

    getRandomNumFromResults(results) {
        let { num: randomResult, isMain } = this.getRandomResultAssumingThreshold(results);
        if (isMain) {
            this.options.stepsWithoutMainFormula = 0;
        } else {
            this.options.stepsWithoutMainFormula++;
        }
        return randomResult;
    }

    getJumpOverOperation(resultNum, rules, prevResult) {
        if (rules[rules.length - 1].type !== 'friends' && rules[rules.length - 1].type !== 'brothersAndFriends') {
            return { result: 0, label: 'WITHOUT JUMP OVER' };
        }
        const prevNumArray = prevResult
            .toString()
            .split('')
            .reverse();
        const goOverHundreds = (prevNumArray.length >= 2 ? prevNumArray[2] : 1) * 100;
        const nextValueAtTenthPosition = rules.length - 2 >= 0 ? rules[rules.length - 2].value : 0;
        const nextValueAtHundredsPosition = rules.length - 3 >= 0 ? rules[rules.length - 3].value : 0;
        if (!this.options.onlyNegativeAnswers) {
            if ((Math.floor((prevResult % 100) / 10) + nextValueAtTenthPosition) % 10 === 5 && resultNum % 100 < 50) {
                return { result: 50, label: 'JUMP OVER 50-' };
            }
            if ((Math.floor((prevResult % 100) / 10) + nextValueAtTenthPosition) % 10 === 4 && resultNum % 100 >= 50) {
                return { result: 50, label: 'JUMP OVER 50+' };
            }
            if (
                (rules.length === 2 || (Math.floor(prevResult / 100) % 10) + nextValueAtHundredsPosition === 1) &&
                (Math.floor(prevResult / 10) % 10) + nextValueAtTenthPosition === 0 &&
                resultNum % 1000 < goOverHundreds
            ) {
                return { result: 100, label: 'JUMP OVER 100-' };
            }
            if (
                (Math.floor(prevResult / 10) % 10) + nextValueAtTenthPosition === 9 &&
                resultNum % 1000 > goOverHundreds
            ) {
                return { result: 100, label: 'JUMP OVER 100+' };
            }
            return { result: 0, label: 'WITHOUT JUMP OVER' };
        } else {
            if ((Math.ceil((prevResult % 100) / 10) + nextValueAtTenthPosition) % 10 === -5 && resultNum % 100 > -50) {
                return { result: 50, label: 'JUMP OVER 50-' };
            }
            if ((Math.ceil((prevResult % 100) / 10) + nextValueAtTenthPosition) % 10 === -4 && resultNum % 100 <= -50) {
                return { result: 50, label: 'JUMP OVER 50+' };
            }
            if (
                (rules.length === 2 || (Math.ceil(prevResult / 100) % 10) + nextValueAtHundredsPosition === -1) &&
                (Math.ceil(prevResult / 10) % 10) + nextValueAtTenthPosition === 0 &&
                resultNum % 1000 > -goOverHundreds
            ) {
                return { result: 100, label: 'JUMP OVER 100-' };
            }
            if (
                (Math.ceil(prevResult / 10) % 10) + nextValueAtTenthPosition === -9 &&
                resultNum % 1000 < -goOverHundreds
            ) {
                return { result: 100, label: 'JUMP OVER 100+' };
            }
            return { result: 0, label: 'WITHOUT JUMP OVER' };
        }
    }

    digitTransfer(index, num, numArray) {
        let newValue = arrayToNum(numArray) + num * Math.pow(10, index);
        numArray = toString(newValue).split('');
        let isPositiveNumber = numArray[0] !== '-';
        !isPositiveNumber && numArray.splice(0, 1);
        numArray = numArray.map(char => (isPositiveNumber ? +char : -char)).reverse();
        if (this.options.digitsLimit - numArray.length > 0) {
            numArray.push(...Array(this.options.digitsLimit - numArray.length).fill(0));
        }
        return numArray;
    }

    static generateNumsForDigit(digit) {
        let nums = [];
        // toString(-0) = "-0", (-0).toString = "0"
        let isPositive = toString(digit).split('')[0] !== '-';
        for (let value = -9; value <= 9; value++) {
            nums.push({
                value,
                type: isPositive
                    ? Soroban.getTypeOfOperation(digit, value)
                    : Soroban.getTypeOfOperation(-digit, -value),
            });
        }
        return nums;
    }

    generateJumpOverThroughFifty(nums, index) {
        let NUM = this.num % 100;
        if (Math.abs(NUM) > 9) {
            if (Math.abs(NUM) < this.options.jumpOver) {
                if (index === 1) {
                    return this.generateTensForPlusJumpOverThroughFifty(nums, NUM);
                }
                if (index === 0) {
                    return this.generateUnitsForPlusJumpOverThroughFifty(nums, NUM);
                }
            } else {
                if (index >= 2) {
                    if (Math.floor(Math.abs(this.num) / Math.pow(10, index)) % 10 !== 0) {
                        return [
                            {
                                type: 'simple',
                                value:
                                    NUM >= 0
                                        ? -Math.floor(this.num / Math.pow(10, index)) % 10
                                        : Math.floor(-this.num / Math.pow(10, index)) % 10,
                            },
                        ];
                    } else {
                        this.jumpOverOperations.unshift(0);
                        return nums;
                    }
                }
                if (index === 1) {
                    return this.generateTensForMinusJumpOverThroughFifty(nums, NUM);
                }
                if (index === 0) {
                    return this.generateUnitsForMinusJumpOverThroughFifty(nums, NUM);
                }
            }
        } else {
            this.jumpOverOperations.unshift(0);
            return nums;
        }
    }

    generateJumpOverThroughHundred(nums, index) {
        if (Math.abs(this.num) < this.options.jumpOver) {
            if (index === 1) {
                return this.generateTensForPlusJumpOverThroughHundred(nums);
            }
            if (index === 0) {
                return this.generateUnitsForPlusJumpOverThroughHundred(nums);
            }
        } else {
            if (index > 2) {
                if (Math.floor(Math.abs(this.num) / Math.pow(10, index)) % 10 !== 0) {
                    return [
                        {
                            type: 'simple',
                            value:
                                this.num >= 0
                                    ? -Math.floor(this.num / Math.pow(10, index)) % 10
                                    : Math.floor(-this.num / Math.pow(10, index)) % 10,
                        },
                    ];
                } else {
                    this.jumpOverOperations.unshift(0);
                    return nums;
                }
            }
            if (index === 2) {
                if (
                    Math.floor(Math.abs(this.num) / 100) % 10 !== 1 &&
                    Math.floor(Math.abs(this.num) / 100) % 10 !== 0
                ) {
                    return [
                        {
                            type: 'simple',
                            value:
                                this.num >= 0
                                    ? (-Math.floor(this.num / 100) % 10) + 1
                                    : (Math.floor(-this.num / 100) % 10) - 1,
                        },
                    ];
                } else {
                    this.jumpOverOperations.unshift(0);
                    return nums;
                }
            }
            if (index === 1) {
                return this.generateTensForMinusJumpOverThroughHundred(nums);
            }
            if (index === 0) {
                return this.generateUnitsForMinusJumpOverThroughHundred(nums);
            }
        }
    }

    generateUnitsForPlusJumpOverThroughFifty(nums, NUM) {
        let searchNums = nums.filter(num => {
            switch (num.type) {
                case 'friends':
                    return NUM > 0 ? num.value > 0 : num.value < 0;
                case 'brothersAndFriends':
                    return NUM > 0 ? num.value > 0 : num.value < 0;
                default:
                    return false;
            }
        });
        if (isEmpty(searchNums) || Math.floor(Math.abs(NUM) / 10) % 10 === 4 || NUM % 10 === 0) {
            this.jumpOverOperations.unshift(0);
            return nums;
        } else {
            return searchNums;
        }
    }

    generateTensForPlusJumpOverThroughFifty(nums, NUM) {
        return Math.floor(Math.abs(NUM) / 10) % 10 !== 4 && NUM % 10 !== 0
            ? [{ type: 'simple', value: NUM >= 0 ? 4 - (Math.floor(NUM / 10) % 10) : (Math.floor(-NUM / 10) % 10) - 4 }]
            : nums;
    }

    generateUnitsForMinusJumpOverThroughFifty(nums, NUM) {
        let searchNums = nums.filter(num => {
            switch (num.type) {
                case 'friends':
                    return NUM > 0 ? num.value < 0 : num.value > 0;
                case 'brothersAndFriends':
                    return NUM > 0 ? num.value < 0 : num.value > 0;
                default:
                    return false;
            }
        });
        if (isEmpty(searchNums) || Math.abs(NUM) % 10 === 9 || Math.floor(Math.abs(NUM) / 10) % 10 === 5) {
            this.jumpOverOperations.unshift(0);
            return nums;
        } else {
            return searchNums;
        }
    }

    generateTensForMinusJumpOverThroughFifty(nums, NUM) {
        return Math.abs(NUM) % 10 !== 9 && Math.floor(Math.abs(NUM) / 10) % 10 !== 5
            ? [{ type: 'simple', value: NUM > 0 ? 5 - (Math.floor(NUM / 10) % 10) : (Math.floor(-NUM / 10) % 10) - 5 }]
            : nums;
    }

    generateUnitsForPlusJumpOverThroughHundred(nums) {
        let searchNums = nums.filter(num => {
            switch (num.type) {
                case 'friends':
                    return this.num > 0 ? num.value > 0 : num.value < 0;
                case 'brothersAndFriends':
                    return this.num > 0 ? num.value > 0 : num.value < 0;
                default:
                    return false;
            }
        });
        if (isEmpty(searchNums)) {
            this.jumpOverOperations.unshift(0);
            return nums;
        } else {
            return searchNums;
        }
    }

    generateTensForPlusJumpOverThroughHundred(nums) {
        return Math.floor(Math.abs(this.num) / 10) % 10 !== 9 && this.num % 10 !== 0
            ? [
                  {
                      type: 'simple',
                      value:
                          this.num >= 0 ? 9 - (Math.floor(this.num / 10) % 10) : (Math.floor(-this.num / 10) % 10) - 9,
                  },
              ]
            : nums;
    }

    generateUnitsForMinusJumpOverThroughHundred(nums) {
        let searchNums = nums.filter(num => {
            switch (num.type) {
                case 'friends':
                    return this.num > 0 ? num.value < 0 : num.value > 0;
                case 'brothersAndFriends':
                    return this.num > 0 ? num.value < 0 : num.value > 0;
                default:
                    return false;
            }
        });
        if (isEmpty(searchNums)) {
            this.jumpOverOperations.unshift(0);
            return nums;
        } else {
            return searchNums;
        }
    }

    generateTensForMinusJumpOverThroughHundred(nums) {
        return Math.abs(this.num) % 10 !== 9 && Math.floor(Math.abs(this.num) / 10) % 10 !== 0
            ? [
                  {
                      type: 'simple',
                      value: this.num > 0 ? -(Math.floor(this.num / 10) % 10) : Math.floor(-this.num / 10) % 10,
                  },
              ]
            : nums;
    }
};
